/**
 * GPT (GUID Partition Table) parser.
 *
 * Reads:
 *   - LBA 1: GPT Header (512 bytes used out of one logical block)
 *   - LBA 2..33: 128 entries × 128 bytes
 *   - Last LBA: secondary GPT (cross-check)
 *
 * Verifies header CRC32 and entries CRC32 (via __CRC32 shared util).
 *
 * Surface: window.__DiskImage.parseGpt(reader) → Promise<{
 *   header: { diskGuid, currentLba, backupLba, firstUsable, lastUsable, partitionEntryLba, numEntries, sizeOfEntry, headerCrc, computedHeaderCrc, entriesCrc, computedEntriesCrc, signatureOk, crcOk },
 *   partitions: GptPartition[],
 *   secondary?: { ok, mismatch },
 *   logicalBlockSize: number
 * }>
 *
 * GptPartition: {
 *   index, typeGuid, typeName, partitionGuid, startLba, endLba, attributes, name
 * }
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.parseGpt) return;

    var CRC32 = w.__CRC32 || null;

    var TYPE_GUIDS = {
        '00000000-0000-0000-0000-000000000000': 'Unused',
        '024dee41-33e7-11d3-9d69-0008c781f39f': 'MBR partition scheme',
        'c12a7328-f81f-11d2-ba4b-00a0c93ec93b': 'EFI System Partition',
        '21686148-6449-6e6f-744e-656564454649': 'BIOS Boot Partition',
        'd3bfe2de-3daf-11df-ba40-e3a556d89593': 'Intel Fast Flash',
        'f4019732-066e-4e12-8273-346c5641494f': 'Sony boot',
        'bfbfafe7-a34f-448a-9a5b-6213eb736c22': 'Lenovo boot',
        // Windows
        'e3c9e316-0b5c-4db8-817d-f92df00215ae': 'Microsoft Reserved',
        'ebd0a0a2-b9e5-4433-87c0-68b6b72699c7': 'Microsoft Basic Data',
        '5808c8aa-7e8f-42e0-85d2-e1e90434cfb3': 'Logical Disk Manager metadata',
        'af9b60a0-1431-4f62-bc68-3311714a69ad': 'Logical Disk Manager data',
        'de94bba4-06d1-4d40-a16a-bfd50179d6ac': 'Windows Recovery',
        '37affc90-ef7d-4e96-91c3-2d7ae055b174': 'IBM GPFS',
        'e75caf8f-f680-4cee-afa3-b001e56efc2d': 'Storage Spaces',
        '558d43c5-a1ac-43c0-aac8-d1472b2923d1': 'Storage Replica',
        // Linux
        '0fc63daf-8483-4772-8e79-3d69d8477de4': 'Linux filesystem',
        'a19d880f-05fc-4d3b-a006-743f0f84911e': 'Linux RAID',
        '0657fd6d-a4ab-43c4-84e5-0933c84b4f4f': 'Linux swap',
        'e6d6d379-f507-44c2-a23c-238f2a3df928': 'Linux LVM',
        '933ac7e1-2eb4-4f13-b844-0e14e2aef915': 'Linux home',
        '3b8f8425-20e0-4f3b-907f-1a25a76f98e8': 'Linux server data',
        '4f68bce3-e8cd-4db1-96e7-fbcaf984b709': 'Linux x86-64 root (/)',
        '69dad710-2ce4-4e3c-b16c-21a1d49abed3': 'Linux ARM64 root (/)',
        '993d8d3d-f80e-4225-855a-9daf8ed7ea97': 'Linux ARM32 root (/)',
        '7ffec5c9-2d00-49b7-8941-3ea10a5586b7': 'Linux dm-crypt plain',
        'ca7d7ccb-63ed-4c53-861c-1742536059cc': 'Linux LUKS',
        '8da63339-0007-60c0-c436-083ac8230908': 'Linux reserved',
        // Apple
        '48465300-0000-11aa-aa11-00306543ecac': 'Apple HFS+',
        '7c3457ef-0000-11aa-aa11-00306543ecac': 'Apple APFS',
        '55465300-0000-11aa-aa11-00306543ecac': 'Apple UFS',
        '52414944-0000-11aa-aa11-00306543ecac': 'Apple RAID',
        '52414944-5f4f-11aa-aa11-00306543ecac': 'Apple RAID offline',
        '426f6f74-0000-11aa-aa11-00306543ecac': 'Apple Boot',
        '4c616265-6c00-11aa-aa11-00306543ecac': 'Apple Label',
        '5265636f-7665-11aa-aa11-00306543ecac': 'Apple TV Recovery',
        '53746f72-6167-11aa-aa11-00306543ecac': 'Apple Core Storage',
        // BSD
        '516e7cb4-6ecf-11d6-8ff8-00022d09712b': 'FreeBSD',
        '83bd6b9d-7f41-11dc-be0b-001560b84f0f': 'FreeBSD boot',
        '516e7cb5-6ecf-11d6-8ff8-00022d09712b': 'FreeBSD swap',
        '516e7cb6-6ecf-11d6-8ff8-00022d09712b': 'FreeBSD UFS',
        '516e7cb8-6ecf-11d6-8ff8-00022d09712b': 'FreeBSD Vinum',
        '516e7cba-6ecf-11d6-8ff8-00022d09712b': 'FreeBSD ZFS',
        // ChromeOS
        'cab6e88e-abf3-4102-a07a-d4bb9be3c1d3': 'ChromeOS firmware',
        'fe3a2a5d-4f32-41a7-b725-accc3285a309': 'ChromeOS kernel',
        '3cb8e202-3b7e-47dd-8a3c-7ff2a13cfcec': 'ChromeOS rootfs',
        '2e0a753d-9e48-43b0-8337-b15192cb1b5e': 'ChromeOS reserved'
    };

    function _u32le(b, off) { return ((b[off] | (b[off + 1] << 8) | (b[off + 2] << 16) | (b[off + 3] << 24)) >>> 0); }
    function _u64le(b, off) {
        var lo = _u32le(b, off);
        var hi = _u32le(b, off + 4);
        return hi * 0x100000000 + lo;
    }

    function _hex(n, w2) { var s = (n >>> 0).toString(16); while (s.length < w2) s = '0' + s; return s; }

    function _decodeGuid(b, off) {
        // Mixed-endian: first 4 bytes LE, next 2 LE, next 2 LE, last 8 BE
        var d1 = _u32le(b, off);
        var d2 = (b[off + 4] | (b[off + 5] << 8));
        var d3 = (b[off + 6] | (b[off + 7] << 8));
        var d4 = '';
        for (var i = 0; i < 2; i++) d4 += _hex(b[off + 8 + i], 2);
        var d5 = '';
        for (var j = 0; j < 6; j++) d5 += _hex(b[off + 10 + j], 2);
        return _hex(d1, 8) + '-' + _hex(d2, 4) + '-' + _hex(d3, 4) + '-' + d4 + '-' + d5;
    }

    function _decodeUtf16leTrim(b, off, len) {
        // Read 36-char Partition Name, trim trailing NULs
        var sub = b.subarray(off, off + len);
        var s = new TextDecoder('utf-16le', { fatal: false }).decode(sub);
        var nul = s.indexOf('\u0000');
        return nul >= 0 ? s.slice(0, nul) : s;
    }

    function parseGpt(reader, opts) {
        opts = opts || {};
        var lbs = opts.logicalBlockSize || 512;

        return reader.bytes(lbs, lbs).then(function (header) {
            var sig = String.fromCharCode(header[0], header[1], header[2], header[3], header[4], header[5], header[6], header[7]);
            var signatureOk = (sig === 'EFI PART');

            var hdrSize = _u32le(header, 12);
            var headerCrc = _u32le(header, 16);
            var currentLba = _u64le(header, 24);
            var backupLba = _u64le(header, 32);
            var firstUsable = _u64le(header, 40);
            var lastUsable = _u64le(header, 48);
            var diskGuid = _decodeGuid(header, 56);
            var partitionEntryLba = _u64le(header, 72);
            var numEntries = _u32le(header, 80);
            var sizeOfEntry = _u32le(header, 84);
            var entriesCrc = _u32le(header, 88);

            var computedHeaderCrc = null;
            if (CRC32) {
                // CRC of header[0..hdrSize] with bytes 16..19 zeroed
                var hdrForCrc = new Uint8Array(hdrSize);
                hdrForCrc.set(header.subarray(0, hdrSize), 0);
                hdrForCrc[16] = 0; hdrForCrc[17] = 0; hdrForCrc[18] = 0; hdrForCrc[19] = 0;
                computedHeaderCrc = CRC32.bytes(0, hdrForCrc);
            }
            var crcOk = (computedHeaderCrc != null) ? (computedHeaderCrc === headerCrc) : null;

            // Read entries array (numEntries × sizeOfEntry bytes)
            var entriesByteLen = numEntries * sizeOfEntry;
            return reader.bytes(partitionEntryLba * lbs, entriesByteLen).then(function (eb) {
                var partitions = [];
                for (var i = 0; i < numEntries; i++) {
                    var off = i * sizeOfEntry;
                    var typeGuid = _decodeGuid(eb, off);
                    if (typeGuid === '00000000-0000-0000-0000-000000000000') continue;
                    partitions.push({
                        index: i,
                        typeGuid: typeGuid,
                        typeName: TYPE_GUIDS[typeGuid] || ('Unknown ' + typeGuid),
                        partitionGuid: _decodeGuid(eb, off + 16),
                        startLba: _u64le(eb, off + 32),
                        endLba: _u64le(eb, off + 40),
                        attributes: _u64le(eb, off + 48),
                        name: _decodeUtf16leTrim(eb, off + 56, 72),
                        sizeLba: _u64le(eb, off + 40) - _u64le(eb, off + 32) + 1,
                        sizeBytes: (_u64le(eb, off + 40) - _u64le(eb, off + 32) + 1) * lbs
                    });
                }
                var computedEntriesCrc = null;
                if (CRC32) computedEntriesCrc = CRC32.bytes(0, eb);
                var entriesCrcOk = computedEntriesCrc != null ? computedEntriesCrc === entriesCrc : null;

                return {
                    header: {
                        diskGuid: diskGuid,
                        currentLba: currentLba,
                        backupLba: backupLba,
                        firstUsable: firstUsable,
                        lastUsable: lastUsable,
                        partitionEntryLba: partitionEntryLba,
                        numEntries: numEntries,
                        sizeOfEntry: sizeOfEntry,
                        headerCrc: headerCrc,
                        computedHeaderCrc: computedHeaderCrc,
                        entriesCrc: entriesCrc,
                        computedEntriesCrc: computedEntriesCrc,
                        signatureOk: signatureOk,
                        crcOk: crcOk,
                        entriesCrcOk: entriesCrcOk,
                        revision: _u32le(header, 8)
                    },
                    partitions: partitions,
                    logicalBlockSize: lbs
                };
            });
        });
    }

    w.__DiskImage.parseGpt = parseGpt;
    w.__DiskImage.GPT_TYPES = TYPE_GUIDS;
})(window);
